/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

export type FilterFieldOption = { value: string; label: string };

export type FilterField = {
  key: string;
  label: string;
  kind: "choice" | "date";
  options?: FilterFieldOption[];
  searchable?: boolean | undefined;
  scim: (value: string) => string;
};

export type ActiveFilter = { key: string; value: string };

export function filtersToScim(fields: FilterField[], active: ActiveFilter[]): string | null {
  const byKey = new Map(fields.map((field) => [field.key, field]));
  const clauses = active
    .map((filter) => {
      const field = byKey.get(filter.key);
      return field ? field.scim(escapeValue(filter.value)) : null;
    })
    .filter((clause): clause is string => clause !== null);
  return clauses.length === 0 ? null : clauses.join(" and ");
}

function escapeValue(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export function filtersFromParams(
  fields: FilterField[],
  params: URLSearchParams,
): ActiveFilter[] {
  const known = new Set(fields.map((field) => field.key));
  const active: ActiveFilter[] = [];
  for (const [key, value] of params) {
    if (known.has(key) && value !== "" && !active.some((filter) => filter.key === key)) {
      active.push({ key, value });
    }
  }
  return active;
}

export function paramsWithFilters(
  params: URLSearchParams,
  fields: FilterField[],
  active: ActiveFilter[],
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const field of fields) {
    next.delete(field.key);
  }
  for (const filter of active) {
    next.set(filter.key, filter.value);
  }
  return next;
}

export function filterValueLabel(field: FilterField, value: string): string {
  if (field.kind === "date") {
    return toDateValue(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return field.options?.find((option) => option.value === value)?.label ?? value;
}

export function toYmd(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function toDateValue(ymd: string): Date {
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

export function localDayStartIso(ymd: string): string {
  return toDateValue(ymd).toISOString();
}

export function localDayEndIso(ymd: string): string {
  const date = toDateValue(ymd);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
}
